import React, { useState, useEffect } from 'react';
import { DataView } from 'primereact/dataview';
import { Tag } from 'primereact/tag';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Dialog } from 'primereact/dialog';
import { format } from 'date-fns';
import '../../styles/VisitPanel.css';
import { auth, db } from '../../config/firebase';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { isAfter, isBefore, isEqual, parseISO } from 'date-fns';

const VisitPanel = () => {
    const [visits, setVisits] = useState([]);
    const [selectedVisit, setSelectedVisit] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const currentUser = auth.currentUser;

    useEffect(() => {
        if (!currentUser) return;

        // Set up real-time listener for visits
        const setupVisitsListener = async () => {
            try {
                setLoading(true);
                
                // Get user info to check for partner
                const userInfoDoc = await getDoc(doc(db, "UserInformation", currentUser.uid));
                let partnerId = null;
                
                if (userInfoDoc.exists()) {
                    const userData = userInfoDoc.data();
                    partnerId = userData.inRelationWith;
                }

                // Set up listener for current user's visits
                const userVisitsRef = collection(db, "events", currentUser.uid, "userVisits");
                const unsubscribeUser = onSnapshot(userVisitsRef, (snapshot) => {
                    const userVisits = [];
                    snapshot.forEach(doc => {
                        const data = doc.data();
                        userVisits.push({
                            id: doc.id,
                            ...data,
                            start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                            end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                            owner: 'You'
                        });
                    });

                    // Update visits with user's visits
                    setVisits(currentVisits => {
                        const partnerVisits = currentVisits.filter(visit => visit.owner === 'Partner');
                        const allVisits = [...userVisits, ...partnerVisits];
                        
                        const today = new Date();
                        const filtered = allVisits.filter(v => {
                            const end = v.end ? new Date(v.end) : null;
                            return end && (isAfter(end, today) || isEqual(end, today));
                        });
                        
                        return filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
                    });
                    
                    setLoading(false);
                }, (error) => {
                    console.error("Error listening to user visits:", error);
                    setLoading(false);
                });

                // Set up listener for partner's visits if partner exists
                let unsubscribePartner = null;
                if (partnerId) {
                    const partnerVisitsRef = collection(db, "events", partnerId, "userVisits");
                    unsubscribePartner = onSnapshot(partnerVisitsRef, (snapshot) => {
                        const partnerVisits = [];
                        snapshot.forEach(doc => {
                            const data = doc.data();
                            partnerVisits.push({
                                id: doc.id,
                                ...data,
                                start: data.start?.toDate ? data.start.toDate() : new Date(data.start),
                                end: data.end?.toDate ? data.end.toDate() : new Date(data.end),
                                owner: 'Partner'
                            });
                        });

                        // Update visits with partner's visits
                        setVisits(currentVisits => {
                            const userVisits = currentVisits.filter(visit => visit.owner === 'You');
                            const allVisits = [...userVisits, ...partnerVisits];
                            
                            const today = new Date();
                            const filtered = allVisits.filter(v => {
                                const end = v.end ? new Date(v.end) : null;
                                return end && (isAfter(end, today) || isEqual(end, today));
                            });
                            
                            return filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
                        });
                    }, (error) => {
                        console.error("Error listening to partner visits:", error);
                    });
                }

                // Return cleanup function
                return () => {
                    unsubscribeUser();
                    if (unsubscribePartner) {
                        unsubscribePartner();
                    }
                };
            } catch (error) {
                console.error('Error setting up visits listeners:', error);
                setLoading(false);
            }
        };

        const cleanup = setupVisitsListener();
        
        // Return cleanup function
        return () => {
            cleanup.then(cleanupFn => {
                if (cleanupFn) cleanupFn();
            });
        };
    }, [currentUser]);

    const formatDate = (date) => {
        try {
            const d = date instanceof Date ? date : date?.toDate?.() || new Date(date);
            return format(d, 'MMM dd, yyyy');
        } catch {
            return 'Invalid date';
        }
    };

    // Replace PrimeReact Tag with custom pill button
    const PillButton = ({ label }) => (
        <span className="visit-pill">{label}</span>
    );

    // Update renderReminders to use PillButton
    const renderReminders = (reminders) => {
        const hasReminders = reminders?.oneWeekBefore || reminders?.twoDaysBefore;
        return hasReminders ? (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                {reminders.oneWeekBefore && <PillButton label="One week before" />}
                {reminders.twoDaysBefore && <PillButton label="Two days before" />}
            </div>
        ) : 'No reminders set';
    };

    const renderDailyPlans = () => {
        const plans = selectedVisit?.dailyPlans;
        if (!plans || plans.length === 0) return <div>No daily plans available</div>;

        return (
            <Accordion className="visit-accordion">
                {plans.map((plan, index) => (
                    <AccordionTab key={index} header={formatDate(plan.date)} className="visit-accordion-tab">
                        <div className="visit-detail-note">{plan.notes}</div>
                        {plan.bulletPoints?.length > 0 && (
                            <div style={{ marginLeft: '2rem' }}>
                            <ul>
                                {plan.bulletPoints.map((point, idx) =>
                                    point && <li key={idx}>{point}</li>
                                )}
                            </ul>
                            </div>
                        )}
                    </AccordionTab>
                ))}
            </Accordion>
        );
    };

    // Helper to check if a visit is 'current' (today is within start and end, inclusive)
    const isCurrentVisit = (visit) => {
        const today = new Date();
        const start = visit.start ? new Date(visit.start) : null;
        const end = visit.end ? new Date(visit.end) : null;
        if (!start || !end) return false;
        return (
            (isEqual(today, start) || isEqual(today, end)) ||
            (isAfter(today, start) && isBefore(today, end))
        );
    };

    const renderVisitItem = (visit) => (
        <div
            className={`visit-card-item${isCurrentVisit(visit) ? ' current-visit' : ''}`}
            onClick={() => {
                setSelectedVisit(visit);
                setModalVisible(true);
            }}
        >
            <div className="visit-card-title">{visit.title || 'Untitled Visit'}</div>
            {visit.start && (
                <div className="visit-card-date">{formatDate(visit.start)}</div>
            )}
        </div>
    );

    return (
        <div className="visit-panel-container">
            <div className="visit-card-wrapper">
                <h3 className="visit-card-header">Upcoming Visits</h3>
                {loading ? (
                    <div className="visit-empty">Loading visits...</div>
                ) : visits.length === 0 ? (
                    <div className="visit-empty">No visits scheduled</div>
                ) : (
                    <DataView
                        value={visits}
                        itemTemplate={renderVisitItem}
                        paginator={visits.length > 5}
                        rows={5}
                    />
                )}
            </div>

            <Dialog
                header={null}
                visible={modalVisible}
                style={{ width: '90%', maxWidth: '800px' }}
                onHide={() => setModalVisible(false)}
                className="visit-dialog"
                draggable={false}
                resizable={false}
                modal
            >
                {selectedVisit && (
                    <>
                        <h1 className="visit-dialog-title">{selectedVisit.title || 'Untitled Visit'}</h1>                        <button className="visit-dialog-close" onClick={() => setModalVisible(false)}>✕</button>
                        <div className="visit-dialog-content">
                            <div className="visit-section">
                                <h2 className="visit-section-title">Visit Period</h2>
                                <div className="visit-section-dates">{`${formatDate(selectedVisit.start)} - ${formatDate(selectedVisit.end)}`}</div>
                            </div>
                            {selectedVisit.description && (
                                <div className="visit-section">
                                    <h2 className="visit-section-title">Description</h2>
                                    <div className="visit-section-desc">{selectedVisit.description}</div>
                                </div>
                            )}
                            <div className="visit-section">
                                <h2 className="visit-section-title">Reminders</h2>
                                <div>{renderReminders(selectedVisit.reminders)}</div>
                            </div>
                            <div className="visit-section">
                                <h2 className="visit-section-title">Daily Plans</h2>
                                {renderDailyPlans()}
                            </div>
                        </div>
                    </>
                )}
            </Dialog>
        </div>
    );
};

export default VisitPanel;
